import * as THREE from 'three'
import type { FragmentsModel, MaterialDefinition } from '@thatopen/fragments'
import type * as OBC from '@thatopen/components'
import type * as OBF from '@thatopen/components-front'
import { useAppStore } from '@core/store'
import { avisarMudancaDeVisibilidade } from './sinalDaVisibilidade'
import { rawIfc } from './rawIfc'
import { fragmentsReturnsWorld, resolveRobustFitBox } from './geoTransform'
import { isFeatureEnabled } from '@core/features'
import { aplicarEstiloApresentacao } from '@viewer/canvas/estiloApresentacao'
import {
  aplicarContornos,
  escondidosPeloFantasmaEm,
  esquecerModelo,
  largarContornos,
  type EstiloDoFantasma,
} from './ghostContornos'

type World = OBC.SimpleWorld<
  OBC.SimpleScene,
  OBC.SimpleCamera,
  OBF.RendererWith2D
>

// FragmentsModel and Highlighter instances are not serializable, so they
// cannot live in zustand. This singleton is populated by useViewer on
// init/load and consumed by inspector panels to call methods like
// getItemsData, getSpatialStructure, etc.
const models = new Map<string, FragmentsModel>()
const ancestorPaths = new Map<string, Map<number, number[]>>()
const descendantsMap = new Map<string, Map<number, number[]>>()
let highlighter: OBF.Highlighter | null = null
let world: World | null = null
let grids: OBC.Grids | null = null
const listeners = new Set<() => void>()

// Per-element selective transparency (the "fade" highlighter style).
// Tracked here as a per-model Set so subsequent makeTransparent calls
// add to the existing set rather than replacing it (additive workflow,
// the same pattern as hideSelected). clearTransparent wipes everything.
const transparentItems = new Map<string, Set<number>>()
const materialFadeStyles = new Set<string>()

/** As IfcSpatialZone de cada modelo, pintadas com o estilo
 *  ESTILO_DAS_ZONAS. Guardadas para se poder voltar a pintar e para
 *  sair quando o modelo sai. */
const zonasPorModelo = new Map<string, Set<number>>()
export const ESTILO_DAS_ZONAS = 'vn2r-zona'

/** Fantasma fixo vindo das Lentes (acção "Fantasma"): por modelo, os
 *  elementos que ficam em fantasma sem precisar de selecção. */
const fantasmaDaLente = new Map<string, Set<number>>()
/** Ficou alguma coisa em fantasma da última vez? Para saber se é preciso
 *  limpar quando o fantasma deixa de ser pedido. */
let fantasmaAplicado = false

/** Há fantasma fixo (modelos inteiros ou regras das Lentes)? */
function haFantasmaFixo(): boolean {
  if (useAppStore.getState().modelosEmFantasma.length > 0) return true
  for (const s of fantasmaDaLente.values()) if (s.size > 0) return true
  return false
}

/** O fantasma tem de ser (re)calculado? */
function precisaDeFantasma(): boolean {
  return useAppStore.getState().ghostMode || haFantasmaFixo() || fantasmaAplicado
}

/** O estilo do fantasma que vale agora: contornos só com as arestas
 *  ligadas, senão translúcido (ver ghostContornos.ts). */
function estiloDoFantasmaEmUso(): EstiloDoFantasma {
  const s = useAppStore.getState()
  return s.ghostEstilo === 'contornos' && s.edgesVisible ? 'contornos' : 'translucido'
}

async function clearMaterialFadeStyles(): Promise<void> {
  if (!highlighter) {
    materialFadeStyles.clear()
    return
  }
  for (const style of materialFadeStyles) {
    try {
      await highlighter.clear(style)
    } catch {
      // The style may have been cleared while a model was disposed.
    }
    highlighter.styles.delete(style)
  }
  materialFadeStyles.clear()
}

// Fetch per-item bounding boxes in WORLD coords. A versão atual do
// Fragments devolve as caixas per-item JÁ em coordenadas de mundo
// (fragmentsReturnsWorld); versões/estados anteriores devolviam no
// frame LOCAL do modelo. Aplicar matrixWorld cegamente duplicava o
// transform geo (Focar/Centrar/orbit voavam para 2× da posição real
// em modelos georreferenciados). Returns the same shape as
// model.getBoxes: empty boxes stay empty.
async function worldBoxesFor(
  model: FragmentsModel,
  ids: number[],
): Promise<THREE.Box3[]> {
  const boxes = await model.getBoxes(ids).catch(() => [] as THREE.Box3[])
  if (fragmentsReturnsWorld(model)) return boxes
  model.object.updateMatrixWorld(true)
  const mw = model.object.matrixWorld
  return boxes.map((b) =>
    b && !b.isEmpty() ? b.clone().applyMatrix4(mw) : b,
  )
}

// useViewer registers its loadIfcFile here on mount so non-canvas
// surfaces (sidebar, drag-drop, scripts) can trigger an IFC load
// without needing a ref to the Viewer3D component.
type IfcLoadOptions = {
  replace?: boolean
  handle?: FileSystemFileHandle | null
}
/** O que aconteceu a uma abertura. O carregador nunca rejeita: uma
 *  falha vem como `{ ok: false, erro }`, e quem chama decide o que
 *  dizer. Antes resolvia sempre sem dizer nada, e quem tinha um
 *  try/catch à volta anunciava sucesso depois de uma falha (o FireBIM,
 *  a reposição da sessão, a ligação partilhada). `modelId` é o id com
 *  que o modelo entrou (pode ter sufixo, se o nome já existia), ou
 *  null quando o ficheiro foi para outro painel (nuvem, IFC5). */
export type ResultadoDaAbertura =
  | { ok: true; modelId: string | null }
  | { ok: false; erro: string }
type IfcLoader = (
  file: File,
  options?: IfcLoadOptions,
) => Promise<ResultadoDaAbertura>
let ifcLoader: IfcLoader | null = null
/** Pede ao Fragments uma passagem de vista (sem forçar). Escrito pelo
 *  `useViewer`, que é quem tem o FragmentsManager. */
let actualizadorDosModelos: (() => void) | null = null

/** Os planos que o renderizador está a aplicar (caixa de corte e planos
 *  soltos). Lidos de novo de cada vez: o `useSectionBox` e as capturas
 *  dos conflitos trocam o array inteiro, não mexem nele. */
function planosDoRenderizador(): THREE.Plane[] {
  return world?.renderer?.three?.clippingPlanes ?? []
}

/** Entrega ao modelo os planos de corte do renderizador.
 *
 *  Sem isto o Fragments não sabia que havia cortes: o raio (pré-selecção,
 *  clique, medições, rectângulo de selecção) apanhava a parte cortada e
 *  invisível dos elementos, e o worker continuava a carregar tiles que
 *  estavam inteiros fora da caixa. Ver docs/CAIXA_DE_CORTE.md.
 *
 *  Usa-se o `getClippingPlanesEvent` e não o `useClippingPlanes`: este
 *  último guarda o ARRAY, e o array do renderizador é substituído a cada
 *  mudança da caixa. A função é lida em cada passagem de vista, por isso
 *  devolve sempre o array actual. */
function ligarCortesAoModelo(model: FragmentsModel): void {
  try {
    ;(
      model as unknown as { getClippingPlanesEvent: () => THREE.Plane[] }
    ).getClippingPlanesEvent = planosDoRenderizador
  } catch {
    /* modelo sem o gancho (duplos de teste) */
  }
}
// Aberturas de modelos em curso, e lotes de aberturas (a recuperação da
// sessão reabre vários IFC seguidos). Quem lê os modelos em segundo plano
// (as âncoras dos pins BCF) espera que isto chegue a zero: o Fragments
// está a montar materiais e tiles, e o worker é o mesmo.
let carregamentos = 0
// Uma abertura de cada vez, e o mesmo ficheiro pedido duas vezes ao
// mesmo tempo e uma so abertura. No primeiro uso real de uma sessao
// partilhada (Vitor, 15/10/2026) a reposicao automatica abria os modelos
// um a um e, ao mesmo tempo, o "Reabrir" de cada linha pedia-os outra
// vez: varias aberturas do mesmo modelo em paralelo no motor e a sessao
// ficava presa em "a abrir..." com o primeiro desenhado.
let filaDeAberturas: Promise<unknown> = Promise.resolve()
const aberturasEmCurso = new Map<string, Promise<ResultadoDaAbertura>>()
/** Quanto a fila espera pela abertura anterior antes de seguir: uma
 *  abertura que nunca acaba nao pode prender as outras para sempre. */
export const ESPERA_MAXIMA_NA_FILA_MS = 120_000
let lotesDeCarregamento = 0

function notify() {
  for (const fn of listeners) fn()
}

// --- History helpers -----------------------------------------------
// Snapshot the per-model hidden id sets so a viewer-wide visibility
// change (hide / isolate / show-all) can be undone by replaying the
// previous state. The data is captured BEFORE the mutation runs.
async function snapshotHidden(): Promise<Map<string, number[]>> {
  const snap = new Map<string, number[]>()
  for (const [modelId, model] of models) {
    try {
      const hidden = await model.getItemsByVisibility(false)
      snap.set(modelId, [...hidden])
    } catch {
      snap.set(modelId, [])
    }
  }
  return snap
}

async function restoreHiddenSnapshot(
  snap: Map<string, number[]>,
): Promise<void> {
  for (const [modelId, hidden] of snap) {
    const model = models.get(modelId)
    if (!model) continue
    try {
      await model.resetVisible()
      if (hidden.length > 0) await model.setVisible(hidden, false)
    } catch (err) {
      console.warn('[viewerApi] restoreHidden failed', err)
    }
  }
  await viewerApi._refreshVisibilityState()
  viewerApi._invalidate()
}

async function pushSnapshotHistory(
  label: string,
  beforeHidden: Map<string, number[]>,
): Promise<void> {
  // Re-executing the original action generically is hard, so redo
  // replays a snapshot taken RIGHT AFTER the mutation. It used to be
  // taken lazily on the first undo, which folded any later action into
  // it ("Refazer Isolar" ended up hiding the isolated element).
  const afterHidden = await snapshotHidden()
  useAppStore.getState().pushHistoryEntry({
    module: 'viewer',
    label,
    modelIds: [...new Set([...beforeHidden.keys(), ...afterHidden.keys()])],
    undo: async () => {
      await restoreHiddenSnapshot(beforeHidden)
    },
    redo: async () => {
      await restoreHiddenSnapshot(afterHidden)
    },
  })
}

function pushVisibilityHistory(
  label: string,
  byModel: Map<string, number[]>,
  visible: boolean,
): void {
  useAppStore.getState().pushHistoryEntry({
    module: 'viewer',
    label,
    modelIds: [...byModel.keys()],
    undo: async () => {
      for (const [modelId, ids] of byModel) {
        const model = models.get(modelId)
        if (!model || ids.length === 0) continue
        try {
          await model.setVisible(ids, !visible)
        } catch (err) {
          console.warn('[viewerApi] history undo failed', err)
        }
      }
      await viewerApi._refreshVisibilityState()
      viewerApi._invalidate()
    },
    redo: async () => {
      for (const [modelId, ids] of byModel) {
        const model = models.get(modelId)
        if (!model || ids.length === 0) continue
        try {
          await model.setVisible(ids, visible)
        } catch (err) {
          console.warn('[viewerApi] history redo failed', err)
        }
      }
      await viewerApi._refreshVisibilityState()
      viewerApi._invalidate()
    },
  })
}

export const viewerApi = {
  /** Push the current per-item visibility state of every loaded model
   *  to the edgeVisibility DataTexture so the edge shader discards
   *  fragments of hidden items. Translates Fragments localIds to
   *  itemIndices (same id space the geometry's per-vertex `id`
   *  attribute uses). Best-effort: a failed translation leaves edges
   *  for that batch visible. */
  async _refreshEdgesVisibilityTexture(): Promise<void> {
    const { refreshVisibilityFor } = await import(
      '@viewer/lib/edgeVisibility'
    )
    const paraIndices = async (model: FragmentsModel, localIds: number[]): Promise<number[]> => {
      if (localIds.length === 0) return []
      try {
        const out = (await (
          model as unknown as {
            threads: {
              invoke: (modelId: string, method: string, args: unknown[]) => Promise<number[]>
            }
          }
        ).threads.invoke(model.modelId, 'getItemIdsByLocalIds', [localIds])) as number[]
        return out.filter((i) => typeof i === 'number' && i >= 0)
      } catch (err) {
        console.warn('[viewerApi] edge visibility refresh: localId → itemIndex failed', err)
        return []
      }
    }
    for (const [modelId, model] of models.entries()) {
      let hiddenLocalIds: number[]
      try {
        // O fantasma por contornos não esconde nada: os elementos dele
        // ficam marcados à parte (esbatidos), mais abaixo.
        hiddenLocalIds = await model.getItemsByVisibility(false)
      } catch (err) {
        console.warn(
          '[viewerApi] edge visibility refresh: getItemsByVisibility failed',
          err,
        )
        continue
      }
      let hiddenIndices: number[] = []
      if (hiddenLocalIds.length > 0) {
        try {
          hiddenIndices = (await (
            model as unknown as {
              threads: {
                invoke: (
                  modelId: string,
                  method: string,
                  args: unknown[],
                ) => Promise<number[]>
              }
            }
          ).threads.invoke(model.modelId, 'getItemIdsByLocalIds', [
            hiddenLocalIds,
          ])) as number[]
        } catch (err) {
          console.warn(
            '[viewerApi] edge visibility refresh: localId → itemIndex failed',
            err,
          )
        }
      }
      const esbatidos = await paraIndices(model, [...escondidosPeloFantasmaEm(modelId)])
      refreshVisibilityFor(
        modelId,
        hiddenIndices.filter((i) => typeof i === 'number' && i >= 0),
        esbatidos,
      )
    }
    this._invalidate()
    // Quem precisa de saber que elementos se vêem (o preenchimento dos
    // cortes) recalcula: esconder, isolar e o fantasma passam todos aqui.
    avisarMudancaDeVisibilidade()
  },
  registerModel(id: string, model: FragmentsModel) {
    models.set(id, model)
    ligarCortesAoModelo(model)
    notify()
  },
  /** Os planos de corte activos no renderizador (vazio sem cortes). */
  cortesActivos(): THREE.Plane[] {
    return planosDoRenderizador()
  },
  setActualizadorDosModelos(fn: (() => void) | null) {
    actualizadorDosModelos = fn
  },
  /** Faz o Fragments reler a vista (câmara e planos de corte) já, sem
   *  esperar que a câmara se mexa. O raio corre no worker sobre a última
   *  vista recebida, por isso sem isto os planos novos só contavam para
   *  a pré-selecção depois do primeiro movimento de câmara. */
  actualizarModelos(): void {
    try {
      actualizadorDosModelos?.()
    } catch {
      /* sem Fragments iniciado */
    }
  },
  unregisterModel(id: string) {
    esquecerModelo(id)
    fantasmaDaLente.delete(id)
    {
      const s = useAppStore.getState()
      if (s.modelosEmFantasma.includes(id)) s.setModelosEmFantasma(s.modelosEmFantasma.filter((m) => m !== id))
    }
    models.delete(id)
    ancestorPaths.delete(id)
    descendantsMap.delete(id)
    transparentItems.delete(id)
    zonasPorModelo.delete(id)
    // As entradas de desfazer deste modelo saem com ele: o id é o nome do
    // ficheiro, e a versão nova do mesmo ficheiro herdava-as.
    useAppStore.getState().removeHistoryEntriesForModel(id)
    void import('@viewer/lib/unidadesDoModelo').then(({ esquecerUnidades }) =>
      esquecerUnidades(id),
    )
    void import('@viewer/lib/classColors').then(({ forgetModel }) =>
      forgetModel(id),
    )
    void import('@viewer/lib/paintEngine').then(
      ({ detachPaintFromModel }) => detachPaintFromModel(id),
    )
    void import('@viewer/lib/edgeVisibility').then(
      ({ disposeEdgeVisibility }) => disposeEdgeVisibility(id),
    )
    notify()
  },
  // For each localId in the spatial tree, store the chain of ancestor
  // localIds going from the root (IfcProject) down to (but not
  // including) the localId itself. Used by the properties panel to
  // compute element-to-container deltas.
  setAncestorPaths(modelId: string, paths: Map<number, number[]>) {
    ancestorPaths.set(modelId, paths)
    // Build the inverse index too: container localId → leaf localIds
    // contained anywhere beneath it. Used to compute container bbox
    // extents (Fragments does not preserve IfcLocalPlacement, so we
    // approximate the container "position" with the centroid of the
    // bounding boxes of its contained elements).
    const desc = new Map<number, number[]>()
    for (const [childId, chain] of paths) {
      for (const ancestorId of chain) {
        let arr = desc.get(ancestorId)
        if (!arr) {
          arr = []
          desc.set(ancestorId, arr)
        }
        arr.push(childId)
      }
    }
    descendantsMap.set(modelId, desc)
    notify()
  },
  getAncestorPath(modelId: string, localId: number): number[] {
    return ancestorPaths.get(modelId)?.get(localId) ?? []
  },
  getDescendants(modelId: string, localId: number): number[] {
    return descendantsMap.get(modelId)?.get(localId) ?? []
  },
  getModel(id: string): FragmentsModel | undefined {
    return models.get(id)
  },
  // World-space bbox centre per localId, aligned to the input order
  // (null where the item has no geometry / empty box). Used by the diff
  // engine to detect elements whose position changed between versions.
  async getWorldBoxCenters(
    modelId: string,
    localIds: number[],
  ): Promise<Array<[number, number, number] | null>> {
    const model = models.get(modelId)
    if (!model || localIds.length === 0) return localIds.map(() => null)
    const boxes = await worldBoxesFor(model, localIds)
    const c = new THREE.Vector3()
    return boxes.map((b) => {
      if (!b || b.isEmpty()) return null
      b.getCenter(c)
      return [c.x, c.y, c.z] as [number, number, number]
    })
  },
  iterModels(): IterableIterator<[string, FragmentsModel]> {
    return models.entries()
  },
  // Repaint every loaded model with the current 3-layer pipeline
  // (palette + colorBy criterion + manual paints). Default: re-apply
  // layers without resetColor: fast path for tile-spawn refreshes,
  // selection wipes, or any external event that knocks a colour slot
  // out without changing the layer state. Pass { reset: true } when
  // the layers themselves changed (criterion swap, palette toggle,
  // bg swap, manual paint added) so the bitmask is freed first.
  async repaintAllModels(opts: { reset?: boolean } = {}): Promise<void> {
    const { repaintAllModels } = await import('@viewer/lib/classColors')
    try {
      await repaintAllModels(opts)
    } catch (err) {
      console.warn('[viewerApi] repaintAllModels failed', err)
    }
  },
  // ---- Diff overlay (Comparar IFC) ----------------------------------
  // Paint the result of an IFC-to-IFC comparison as the top colour
  // layer: a per-model map of localId → hex. Routed through classColors
  // so the diff survives tile-spawn repaints and the localId → itemIndex
  // translation is handled in one place.
  async applyDiffColors(
    byModel: Map<string, Map<number, string>>,
  ): Promise<void> {
    const { setDiffColors } = await import('@viewer/lib/classColors')
    try {
      await setDiffColors(byModel)
    } catch (err) {
      console.warn('[viewerApi] applyDiffColors failed', err)
    }
  },
  async clearDiffColors(): Promise<void> {
    const { clearDiffColors } = await import('@viewer/lib/classColors')
    try {
      await clearDiffColors()
    } catch (err) {
      console.warn('[viewerApi] clearDiffColors failed', err)
    }
  },
  async repaintOneModel(
    modelId: string,
    opts: { reset?: boolean } = {},
  ): Promise<void> {
    const model = models.get(modelId)
    if (!model) return
    const { repaintOneModel } = await import('@viewer/lib/classColors')
    try {
      await repaintOneModel(model, opts)
    } catch (err) {
      console.warn('[viewerApi] repaintOneModel failed', err)
    }
  },
  setHighlighter(h: OBF.Highlighter | null) {
    highlighter = h
  },
  getHighlighter(): OBF.Highlighter | null {
    return highlighter
  },
  setWorld(w: World | null) {
    world = w
    notify()
  },
  getWorld(): World | null {
    return world
  },
  setGrids(g: OBC.Grids | null) {
    grids = g
  },
  setIfcLoader(fn: IfcLoader | null) {
    ifcLoader = fn
    notify()
  },
  hasIfcLoader(): boolean {
    return ifcLoader !== null
  },
  async loadIfcFile(
    file: File,
    options?: IfcLoadOptions,
  ): Promise<ResultadoDaAbertura> {
    if (!ifcLoader) {
      throw new Error('Viewer ainda não está pronto.')
    }
    const chave = `${file.name}|${file.size}|${options?.replace ? 'substituir' : 'acrescentar'}`
    const igual = aberturasEmCurso.get(chave)
    if (igual) return await igual
    carregamentos += 1
    notify()
    const carregador = ifcLoader
    const anterior = Promise.race([
      filaDeAberturas.catch(() => undefined),
      new Promise((resolve) => setTimeout(resolve, ESPERA_MAXIMA_NA_FILA_MS)),
    ])
    const esta = anterior.then(() => carregador(file, options))
      .finally(() => {
        aberturasEmCurso.delete(chave)
        carregamentos -= 1
        notify()
      })
    aberturasEmCurso.set(chave, esta)
    filaDeAberturas = esta.catch(() => undefined)
    return await esta
  },
  /** Há modelos a abrir (uma abertura em curso, ou um lote de aberturas
   *  ainda por acabar, como a recuperação da sessão). */
  aCarregarModelos(): boolean {
    return carregamentos > 0 || lotesDeCarregamento > 0
  },
  /** Marca o início de um lote de aberturas (vários IFC seguidos): entre
   *  um e o seguinte também conta como "a carregar". Devolve a função que
   *  o fecha (idempotente). */
  iniciarLoteDeCarregamento(): () => void {
    lotesDeCarregamento += 1
    notify()
    let fechado = false
    return () => {
      if (fechado) return
      fechado = true
      lotesDeCarregamento -= 1
      notify()
    }
  },
  /** Serialize a live model to That Open's compressed Fragments format. */
  async getFragmentsBuffer(modelId: string): Promise<ArrayBuffer> {
    const model = models.get(modelId)
    if (!model) throw new Error('O modelo já não está carregado.')
    return await model.getBuffer(false)
  },
  /** Download one live model as a portable `.frag` file. */
  async downloadFragments(modelId: string): Promise<void> {
    const model = models.get(modelId)
    if (!model) throw new Error('O modelo já não está carregado.')
    const buffer = await model.getBuffer(false)
    const sourceName =
      useAppStore.getState().models.find((entry) => entry.id === modelId)
        ?.fileName ?? modelId
    const filename = /\.(ifc|ifczip|frag|fragments)$/i.test(sourceName)
      ? sourceName.replace(/\.(ifc|ifczip|frag|fragments)$/i, '.frag')
      : `${sourceName}.frag`
    const blob = new Blob([buffer], { type: 'application/octet-stream' })
    const url = URL.createObjectURL(blob)
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = filename
    document.body.appendChild(anchor)
    anchor.click()
    anchor.remove()
    setTimeout(() => URL.revokeObjectURL(url), 1000)
  },
  // Apply visibility to the THREE.Object3D root of the model AND
  // mirror to the zustand store so the sidebar reflects the change.
  setModelVisibility(modelId: string, visible: boolean) {
    const model = models.get(modelId)
    if (model) model.object.visible = visible
    useAppStore.getState().setModelVisibility(modelId, visible)
    notify()
  },
  // Set the orbit pivot to the centroid of every currently-selected
  // item across all models. Camera position AND orientation stay put
  // (uses camera-controls' setOrbitPoint, which only repins the
  // pivot via internal compensations to focal offset); the next
  // orbit rotates around the new point. Called from the selection
  // subscription so the pivot tracks selection changes automatically,
  // silently, without surprising the user with a camera move.
  // For an explicit "look at this and pivot here" behaviour, use
  // setOrbitAndLookAtSelection (P / context menu).
  async setOrbitToSelection(): Promise<void> {
    if (!world) return
    const targets = useAppStore.getState().selectedTargets
    if (targets.length === 0) return
    const box = new THREE.Box3()
    let initialised = false
    const byModel = new Map<string, number[]>()
    for (const t of targets) {
      const list = byModel.get(t.modelId) ?? []
      list.push(t.localId)
      byModel.set(t.modelId, list)
    }
    for (const [modelId, ids] of byModel) {
      const model = models.get(modelId)
      if (!model) continue
      const boxes = await worldBoxesFor(model, ids)
      for (const b of boxes) {
        if (!b || b.isEmpty()) continue
        if (!initialised) {
          box.copy(b)
          initialised = true
        } else box.union(b)
      }
    }
    if (!initialised) return
    const center = new THREE.Vector3()
    box.getCenter(center)
    world.camera.controls.setOrbitPoint(center.x, center.y, center.z)
  },
  // Force the SimpleRenderer to repaint on the next animation frame.
  // Per-item setVisible writes to a GPU attribute but the renderer's
  // dirty flag is not raised automatically; without this the change
  // is invisible until the user moves the camera.
  _invalidate(): void {
    if (!world) return
    const renderer = world.renderer as { needsUpdate?: boolean } | null
    if (renderer) renderer.needsUpdate = true
  },
  // ---- Per-item visibility -------------------------------------------
  //
  // Fragments stores a per-item visibility flag inside its mesh manager.
  // setVisible / resetVisible / getItemsByVisibility on the model expose
  // it. We always work on items WITH geometry; non-geometric entities
  // (storeys, projects, sites) have nothing to hide.
  //
  // After every mutation we recount hidden items so the "Mostrar tudo"
  // button can disable itself when there's nothing to restore, and we
  // refresh ghost mode so the overlay tracks the new visibility set.
  async _refreshVisibilityState(): Promise<void> {
    let total = 0
    for (const m of models.values()) {
      try {
        const hidden = await m.getItemsByVisibility(false)
        total += hidden.length
      } catch (err) {
        console.warn('[viewerApi] getItemsByVisibility failed', err)
      }
    }
    useAppStore.getState().setHiddenCount(total)
    // Edges visibility is now filtered per-item by the edge shader
    // (LineBasicMaterial patched to read a per-model visibility
    // DataTexture indexed by the per-vertex `id` attribute). Hidden
    // items have their edges discarded automatically at render time:
    // no app-level toggle dance needed. We just push the current
    // visibility state to the texture.
    void this._refreshEdgesVisibilityTexture()
    if (precisaDeFantasma()) {
      await this._applyGhost()
    }
    this._invalidate()
  },
  async hideSelected(): Promise<void> {
    const targets = useAppStore.getState().selectedTargets
    if (targets.length === 0) return
    const byModel = new Map<string, number[]>()
    for (const t of targets) {
      const arr = byModel.get(t.modelId) ?? []
      arr.push(t.localId)
      byModel.set(t.modelId, arr)
    }
    for (const [modelId, ids] of byModel) {
      const model = models.get(modelId)
      if (!model) continue
      try {
        await model.setVisible(ids, false)
      } catch (err) {
        console.warn('[viewerApi] setVisible(hide) failed', err)
      }
    }
    useAppStore.getState().clearSelection()
    useAppStore.getState().setVisibilityMode('hide')
    await this._refreshVisibilityState()
    pushVisibilityHistory(
      `Esconder ${targets.length} elemento${targets.length === 1 ? '' : 's'}`,
      byModel,
      false,
    )
  },
  async isolateSelected(): Promise<void> {
    const targets = useAppStore.getState().selectedTargets
    if (targets.length === 0) return
    const selectedByModel = new Map<string, Set<number>>()
    for (const t of targets) {
      let s = selectedByModel.get(t.modelId)
      if (!s) {
        s = new Set()
        selectedByModel.set(t.modelId, s)
      }
      s.add(t.localId)
    }
    // Snapshot the currently-hidden items per model BEFORE we change
    // anything; the history undo callback restores it.
    const beforeHidden = await snapshotHidden()
    // One pass per model, no preliminary resetVisible (which doubled
    // the cost of large isolates). We diff the two sides (currently
    // visible vs currently hidden) against the keep set, and hand a
    // single setVisible call per direction.
    await Promise.all(
      Array.from(models.entries()).map(async ([modelId, model]) => {
        try {
          const keep = selectedByModel.get(modelId)
          const visible = await model.getItemsByVisibility(true)
          const hidden = await model.getItemsByVisibility(false)
          if (!keep || keep.size === 0) {
            if (visible.length > 0) await model.setVisible(visible, false)
            return
          }
          // Hide every currently-visible item that is not in keep.
          const toHide = visible.filter((id) => !keep.has(id))
          // Re-show any previously-hidden item that IS in keep (so a
          // user can isolate something they had hidden a moment ago).
          const toShow = hidden.filter((id) => keep.has(id))
          if (toHide.length > 0) await model.setVisible(toHide, false)
          if (toShow.length > 0) await model.setVisible(toShow, true)
        } catch (err) {
          console.warn('[viewerApi] isolate failed for model', modelId, err)
        }
      }),
    )
    useAppStore.getState().setVisibilityMode('isolate')
    await this._refreshVisibilityState()
    await pushSnapshotHistory(
      `Isolar ${targets.length} elemento${targets.length === 1 ? '' : 's'}`,
      beforeHidden,
    )
  },
  async hideUnselected(): Promise<void> {
    // Alias for isolate, kept under a clearer name for the UI.
    return this.isolateSelected()
  },
  async showAll(): Promise<void> {
    const beforeHidden = await snapshotHidden()
    let totalHidden = 0
    for (const ids of beforeHidden.values()) totalHidden += ids.length
    for (const model of models.values()) {
      try {
        await model.resetVisible()
      } catch (err) {
        console.warn('[viewerApi] resetVisible failed', err)
      }
    }
    useAppStore.getState().setVisibilityMode('normal')
    // Re-apply the "Esconder spaces" toggle after a global reset:
    // resetVisible wipes EVERY hide, including the spatial-volumes
    // mask. Without this, a "Mostrar tudo" silently makes the toggle
    // lie (it stays ON in the UI but spaces / annotations come back).
    if (useAppStore.getState().hideSpatialVolumes) {
      await this.setSpatialVolumesHidden(true)
    }
    await this._refreshVisibilityState()
    if (totalHidden > 0) {
      await pushSnapshotHistory(
        `Mostrar tudo (${totalHidden} reposto${totalHidden === 1 ? '' : 's'})`,
        beforeHidden,
      )
    }
  },
  // Replace the current selection with every visible geometric item
  // that is NOT currently selected. Hidden items are excluded so the
  // user doesn't suddenly select things they cannot see.
  async invertSelection(): Promise<void> {
    const cur = useAppStore.getState().selectedTargets
    const curSet = new Set(cur.map((t) => `${t.modelId}:${t.localId}`))
    const next: { modelId: string; localId: number }[] = []
    for (const [modelId, model] of models) {
      try {
        const visible = await model.getItemsByVisibility(true)
        for (const id of visible) {
          if (!curSet.has(`${modelId}:${id}`)) {
            next.push({ modelId, localId: id })
          }
        }
      } catch (err) {
        console.warn(
          '[viewerApi] invertSelection failed for model',
          modelId,
          err,
        )
      }
    }
    useAppStore.getState().applyBulkSelection(next, 'replace')
  },
  // Select every element in the same IFC class as the given target.
  // Default scope is the same model; pass `allModels: true` to walk
  // every loaded IFC.
  async selectAllOfClass(
    modelId: string,
    localId: number,
    options?: { allModels?: boolean; mode?: 'replace' | 'add' },
  ): Promise<{ category: string; count: number } | null> {
    const model = models.get(modelId)
    if (!model) return null
    let category: string | null = null
    try {
      const data = await model.getItemsData([localId], {
        attributesDefault: true,
        relationsDefault: { attributes: false, relations: false },
      })
      const first = data?.[0] as Record<string, unknown> | undefined
      const rawCat = first?._category as
        | string
        | { value?: string }
        | undefined
      if (typeof rawCat === 'string') category = rawCat
      else if (rawCat && typeof rawCat === 'object') {
        category = String(rawCat.value ?? '')
      }
    } catch (err) {
      console.warn('[viewerApi] selectAllOfClass: getItemsData failed', err)
    }
    if (!category) return null
    const cat = category.toUpperCase()
    const targets: { modelId: string; localId: number }[] = []
    const scope = options?.allModels
      ? Array.from(models.entries())
      : [[modelId, model] as [string, FragmentsModel]]
    for (const [mid, m] of scope) {
      try {
        const bucket = await m.getItemsOfCategories([
          new RegExp(`^${cat}$`, 'i'),
        ])
        for (const ids of Object.values(bucket)) {
          if (ids) for (const id of ids) targets.push({ modelId: mid, localId: id })
        }
      } catch (err) {
        console.warn('[viewerApi] selectAllOfClass: getItemsOfCategories failed', err)
      }
    }
    useAppStore.getState().applyBulkSelection(targets, options?.mode ?? 'replace')
    return { category, count: targets.length }
  },
  // Select every element that shares the same IfcType (the
  // IfcRelDefinesByType.RelatingType target). Useful for "select all
  // walls of this Type/Family/Wall Type" Revit-style.
  async selectAllOfSameType(
    modelId: string,
    localId: number,
    options?: { allModels?: boolean; mode?: 'replace' | 'add' },
  ): Promise<{ typeName: string | null; count: number } | null> {
    const model = models.get(modelId)
    if (!model) return null
    let typeId: number | null = null
    let typeName: string | null = null
    try {
      const data = await model.getItemsData([localId], {
        attributesDefault: true,
        relationsDefault: { attributes: true, relations: false },
      })
      const first = data?.[0] as Record<string, unknown> | undefined
      // Fragments serialises IfcRelDefinesByType targets under
      // `IsDefinedBy` (or directly under a relation key for the type).
      // We probe the common shapes: IsTypedBy, IsDefinedBy.RelatingType.
      const isTypedBy = first?.IsTypedBy as unknown
      if (Array.isArray(isTypedBy) && isTypedBy.length > 0) {
        const t = isTypedBy[0] as Record<string, unknown>
        const rid = t?.localId ?? t?.expressID
        if (typeof rid === 'number') typeId = rid
        const nameAttr = t?.Name as { value?: unknown } | undefined
        if (nameAttr && typeof nameAttr === 'object' && 'value' in nameAttr) {
          typeName = String(nameAttr.value ?? '') || null
        }
      }
    } catch (err) {
      console.warn('[viewerApi] selectAllOfSameType: getItemsData failed', err)
    }
    if (typeId === null) return { typeName: null, count: 0 }
    // Now walk every item of the same category and check whether they
    // also link to this type id. We use getItemsByQuery if available;
    // otherwise filter by inverse on each candidate (slow path).
    const targets: { modelId: string; localId: number }[] = []
    const scope = options?.allModels
      ? Array.from(models.entries())
      : [[modelId, model] as [string, FragmentsModel]]
    for (const [mid, m] of scope) {
      try {
        const candidates = await m.getItemsByQuery({
          categories: [/.*/],
          relation: { name: 'IsTypedBy', query: { localIds: [typeId] } },
        } as unknown as Parameters<typeof m.getItemsByQuery>[0])
        if (Array.isArray(candidates)) {
          for (const id of candidates) targets.push({ modelId: mid, localId: id })
        }
      } catch (err) {
        console.warn(
          '[viewerApi] selectAllOfSameType: getItemsByQuery failed',
          err,
        )
      }
    }
    useAppStore.getState().applyBulkSelection(targets, options?.mode ?? 'replace')
    return { typeName, count: targets.length }
  },
  // Select every descendant of the given spatial container. The
  // container is identified by walking ancestors of the seed up to the
  // requested level (storey, building, site, project). 'parent' picks
  // the immediate parent; the rest match by category.
  async selectAllInSpatialContainer(
    modelId: string,
    seedLocalId: number,
    level: 'self' | 'parent' | 'space' | 'storey' | 'building' | 'site' | 'project',
    options?: { mode?: 'replace' | 'add' },
  ): Promise<{ containerName: string | null; count: number } | null> {
    const model = models.get(modelId)
    if (!model) return null
    let containerId: number | null = null
    if (level === 'self') {
      // The seed itself IS the container; select its descendants.
      containerId = seedLocalId
    } else {
    const ancestors = this.getAncestorPath(modelId, seedLocalId)
    if (ancestors.length === 0) return null
    if (level === 'parent') {
      // Direct parent = first entry in the ancestor chain.
      containerId = ancestors[0] ?? null
    } else {
      // Walk ancestors and match by category. Need attributes for each.
      try {
        const data = await model.getItemsData(ancestors, {
          attributesDefault: true,
          relationsDefault: { attributes: false, relations: false },
        })
        const wantUpper =
          level === 'space'
            ? 'IFCSPACE'
            : level === 'storey'
              ? 'IFCBUILDINGSTOREY'
              : level === 'building'
                ? 'IFCBUILDING'
                : level === 'site'
                  ? 'IFCSITE'
                  : 'IFCPROJECT'
        for (let i = 0; i < ancestors.length; i++) {
          const item = data[i] as Record<string, unknown> | undefined
          const rawCat = item?._category as
            | string
            | { value?: string }
            | undefined
          const cat =
            typeof rawCat === 'string'
              ? rawCat
              : rawCat && typeof rawCat === 'object'
                ? String(rawCat.value ?? '')
                : ''
          if (cat.toUpperCase() === wantUpper) {
            containerId = ancestors[i]!
            break
          }
        }
      } catch (err) {
        console.warn(
          '[viewerApi] selectAllInSpatialContainer: getItemsData failed',
          err,
        )
      }
    }
    }
    if (containerId === null) return null
    const descendants = this.getDescendants(modelId, containerId)
    let containerName: string | null = null
    try {
      const cdata = await model.getItemsData([containerId], {
        attributesDefault: true,
        relationsDefault: { attributes: false, relations: false },
      })
      const item = cdata?.[0] as Record<string, unknown> | undefined
      const nameAttr = item?.Name as { value?: unknown } | undefined
      if (nameAttr && typeof nameAttr === 'object' && 'value' in nameAttr) {
        containerName = String(nameAttr.value ?? '') || null
      }
    } catch {
      /* ignore */
    }
    const targets = descendants.map((id) => ({ modelId, localId: id }))
    useAppStore.getState().applyBulkSelection(targets, options?.mode ?? 'replace')
    return { containerName, count: targets.length }
  },
  // ---- Scene-wide visual toggles ------------------------------------
  setGridVisible(on: boolean) {
    useAppStore.getState().setGridVisible(on)
    // ThatOpen's Grids component creates a SimpleGrid (a shader Mesh,
    // not a Three GridHelper) per world. Iterate the grids list
    // directly: the .visible setter on SimpleGrid hides the underlying
    // mesh.
    if (!grids) return
    for (const grid of grids.list.values()) {
      grid.visible = on
    }
  },
  setEdgesVisible(on: boolean) {
    useAppStore.getState().setEdgesVisible(on)
    // Walk every model's object tree and toggle the LineSegments
    // children that useViewer adds for the BIM-style outline. Three's
    // `.traverse` lets us reach descendants inside Fragments tiles.
    for (const model of models.values()) {
      model.object.traverse((o) => {
        if ((o as THREE.LineSegments).isLineSegments) o.visible = on
      })
    }
    // Sem arestas, o fantasma por contornos não teria nada para mostrar:
    // passa a translúcido enquanto estiverem desligadas.
    if (useAppStore.getState().ghostMode) void this._applyGhost()
  },
  // Re-run useViewer's addEdges hook for every model with the threshold
  // currently in the store. Used by the Diagnóstico slider so the user
  // can dial in the threshold without reloading.
  setEdgeThreshold(deg: number): void {
    useAppStore.getState().setEdgeThreshold(deg)
    const showEdges = useAppStore.getState().edgesVisible
    // For each mesh in each model: remove its existing edge LineSegments
    // child (if any), clear the __edgesAdded flag, then rebuild with the
    // new threshold. Memory-safe: we dispose the old EdgesGeometry.
    for (const model of models.values()) {
      const edgeMat = (
        model as unknown as { __edgeMaterial?: THREE.LineBasicMaterial }
      ).__edgeMaterial
      model.object.traverse((o) => {
        const mesh = o as THREE.Mesh & { __edgesAdded?: boolean }
        if (!mesh.isMesh) return
        // Remove existing edge children.
        for (let i = mesh.children.length - 1; i >= 0; i--) {
          const child = mesh.children[i] as THREE.LineSegments
          if (child.isLineSegments) {
            mesh.remove(child)
            try {
              child.geometry.dispose()
            } catch {
              /* ignore */
            }
          }
        }
        mesh.__edgesAdded = false
        // Rebuild for this mesh from the cached source arrays (the
        // original position/index arrays on the GPU-uploaded geometry
        // are deleted by Fragments after upload, so we can't call
        // EdgesGeometry on the live geometry; useViewer snapshots them
        // into __vn2rEdgesSrc the first time addEdges runs).
        if (!edgeMat) return
        const cached = (
          mesh.geometry as THREE.BufferGeometry & {
            __vn2rEdgesSrc?: {
              position: Float32Array
              index: Uint32Array | null
            }
          }
        ).__vn2rEdgesSrc
        if (!cached) return
        try {
          const tmp = new THREE.BufferGeometry()
          tmp.setAttribute(
            'position',
            new THREE.BufferAttribute(cached.position, 3),
          )
          if (cached.index) {
            tmp.setIndex(new THREE.BufferAttribute(cached.index, 1))
          }
          const edgesGeo = new THREE.EdgesGeometry(tmp, deg)
          tmp.dispose()
          const edges = new THREE.LineSegments(edgesGeo, edgeMat)
          edges.frustumCulled = false
          edges.visible = showEdges
          mesh.add(edges)
          mesh.__edgesAdded = true
        } catch {
          /* skip */
        }
      })
    }
    this._invalidate()
  },
  async setBgMode(mode: 'dark' | 'light'): Promise<void> {
    const st = useAppStore.getState()
    st.setBgMode(mode)
    // O estilo de apresentação só existe com o fundo claro: escolher o
    // escuro desliga-o.
    if (mode === 'dark' && st.estiloApresentacao) st.setEstiloApresentacao(false)
    if (world) {
      aplicarEstiloApresentacao(world.scene.three, useAppStore.getState().estiloApresentacao, mode)
    }
    // Edge material colour follows the bg (white over dark, near-black
    // over light). Each model created its own material at load time;
    // we look it up via the __edgeMaterial side-channel set in
    // useViewer's loadIfcFile.
    // Same mid-grey universal rationale as useViewer.
    const edgeColour = mode === 'light' ? 0x374151 : 0x6b7280
    const edgeOpacity = mode === 'light' ? 0.75 : 0.7
    for (const model of models.values()) {
      const mat = (
        model as unknown as { __edgeMaterial?: THREE.LineBasicMaterial }
      ).__edgeMaterial
      if (mat) {
        mat.color.setHex(edgeColour)
        mat.opacity = edgeOpacity
        mat.needsUpdate = true
      }
    }
    // Re-paint with the new bg so palette tones swap between dark
    // and light variants (walls/slabs that were light cream don't
    // disappear on a light background). Reset because the palette
    // colours themselves change between bg modes.
    await this.repaintAllModels({ reset: true })
    this._invalidate()
  },
  // Estilo de apresentação: fundo em degradê e luz de hemisfério. Liga
  // o fundo claro, porque as cores da paleta e das arestas são as dele.
  async setEstiloApresentacao(on: boolean): Promise<void> {
    const st = useAppStore.getState()
    st.setEstiloApresentacao(on)
    if (on && st.bgMode !== 'light') {
      await this.setBgMode('light')
      return
    }
    if (world) aplicarEstiloApresentacao(world.scene.three, on, st.bgMode)
    this._invalidate()
  },
  // Toggle the visibility of IfcSpace / IfcSpatialZone / IfcZone
  // across all loaded models. Walks each model and calls setVisible
  // on the matching localIds. Updates the store flag so the ribbon
  // toggle reflects the state.
  async setSpatialVolumesHidden(hide: boolean): Promise<void> {
    useAppStore.getState().setHideSpatialVolumes(hide)
    const regexes = [
      /^IfcSpace$/i,
      /^IfcSpatialZone$/i,
      /^IfcZone$/i,
      // IfcAnnotation: Revit exports view markers and 2D section
      // markers as IfcAnnotation. They show up as planar shapes
      // floating in the model; typically the user wants them
      // hidden alongside spatial volumes.
      /^IfcAnnotation$/i,
    ]
    for (const model of models.values()) {
      try {
        const bucket = await model.getItemsOfCategories(regexes)
        const ids: number[] = []
        for (const arr of Object.values(bucket)) {
          if (arr) ids.push(...arr)
        }
        if (ids.length > 0) {
          await model.setVisible(ids, !hide)
        }
      } catch (err) {
        console.warn('[viewerApi] setSpatialVolumesHidden failed', err)
      }
    }
    await this._refreshVisibilityState()
    this._invalidate()
  },
  // ---- Ghost mode ----------------------------------------------------
  //
  // When ON, every visible non-selected item gets the 'ghost' highlighter
  // style applied so the selection (which uses the 'select' style) pops
  // out of a translucent grey context.
  async _applyGhost(): Promise<void> {
    const hl = highlighter
    if (!hl) return
    const estado = useAppStore.getState()
    const targets = estado.selectedTargets
    // Dois pedidos de fantasma, que se somam:
    //  - o da selecção (Ghost ligado e alguma coisa seleccionada): o
    //    resto do modelo à volta da selecção;
    //  - o fixo (modelos inteiros, ou regras das Lentes), que não
    //    precisa de selecção.
    // Sem nenhum, limpa o que houver e sai. O que está seleccionado
    // nunca fica em fantasma.
    const daSeleccao = estado.ghostMode && targets.length > 0
    if (!daSeleccao && !haFantasmaFixo()) {
      try {
        await hl.clear('ghost')
      } catch {
        /* ignore */
      }
      await this._largarContornos()
      fantasmaAplicado = false
      this._invalidate()
      return
    }
    const selected = new Set<string>()
    for (const t of targets) selected.add(`${t.modelId}:${t.localId}`)
    const modelosFixos = new Set(estado.modelosEmFantasma)
    const map: Record<string, Set<number>> = {}
    for (const [modelId, model] of models) {
      try {
        const visible = await model.getItemsByVisibility(true)
        const modeloFixo = modelosFixos.has(modelId)
        const daLente = fantasmaDaLente.get(modelId)
        const set = new Set<number>()
        for (const id of visible) {
          if (selected.has(`${modelId}:${id}`)) continue
          if (daSeleccao || modeloFixo || daLente?.has(id)) set.add(id)
        }
        map[modelId] = set
      } catch (err) {
        console.warn('[viewerApi] _applyGhost gather failed', err)
      }
    }
    if (estiloDoFantasmaEmUso() === 'contornos') {
      try {
        await hl.clear('ghost')
      } catch {
        /* ignore */
      }
      let mudou = false
      for (const modelId of models.keys()) {
        if (aplicarContornos(modelId, map[modelId] ?? new Set())) mudou = true
      }
      // A mesma textura esbate as arestas e descarta as faces
      // (edgeVisibility.ts e paintEngine.ts).
      if (mudou) await this._refreshEdgesVisibilityTexture()
      fantasmaAplicado = true
      return
    }
    await this._largarContornos()
    fantasmaAplicado = true
    for (const k of Object.keys(map)) if (map[k].size === 0) delete map[k]
    try {
      await hl.highlightByID('ghost', map, true, false)
    } catch (err) {
      console.warn('[viewerApi] highlightByID(ghost) failed', err)
    }
    this._invalidate()
  },
  /** Tira o fantasma por contornos: faces e arestas voltam ao normal. */
  async _largarContornos(): Promise<void> {
    let mexeu = false
    for (const modelId of models.keys()) {
      if (largarContornos(modelId)) mexeu = true
    }
    if (mexeu) await this._refreshEdgesVisibilityTexture()
  },
  /** Contornos (o resto só com as arestas) ou translúcido. */
  async setGhostEstilo(estilo: EstiloDoFantasma): Promise<void> {
    useAppStore.getState().setGhostEstilo(estilo)
    if (precisaDeFantasma()) await this._applyGhost()
  },
  /** Um modelo inteiro em fantasma (ou não), sem precisar de selecção. */
  async setModeloEmFantasma(modelId: string, on: boolean): Promise<void> {
    const s = useAppStore.getState()
    const actuais = s.modelosEmFantasma.filter((id) => id !== modelId)
    s.setModelosEmFantasma(on ? [...actuais, modelId] : actuais)
    await this._applyGhost()
  },
  /** O fantasma pedido pelas Lentes (acção "Fantasma"). null limpa. */
  async setFantasmaDaLente(mapa: Map<string, Set<number>> | null): Promise<void> {
    fantasmaDaLente.clear()
    for (const [id, ids] of mapa ?? []) if (ids.size > 0) fantasmaDaLente.set(id, new Set(ids))
    await this._applyGhost()
  },
  async setGhostMode(on: boolean): Promise<void> {
    useAppStore.getState().setGhostMode(on)
    const hl = highlighter
    if (!hl) return
    if (on) {
      await this._applyGhost()
    } else {
      // O fantasma fixo (modelos, Lentes) não depende do Ghost: o
      // recálculo tira só o da selecção.
      await this._applyGhost()
    }
  },
  // ---- Selective transparency (fade) --------------------------------
  // Adds the current selection to the "fade" highlighter style. Items
  // keep their original colour/material thanks to
  // preserveOriginalMaterial; only the opacity is overridden, so they
  // become see-through but still read as themselves. Aditivo: each
  // call accumulates into transparentItems.
  //
  // We MUST drop the items out of the 'select' style before applying
  // 'fade': updateColors() inside the Highlighter excludes 'select'
  // members from every other style (getMapWithoutSelection), so a fade
  // applied while the items are still selected would be silently
  // stripped at render time and the user would see no change.
  async makeTransparent(): Promise<void> {
    const targets = useAppStore.getState().selectedTargets
    if (targets.length === 0) return
    if (!highlighter) return
    for (const t of targets) {
      let set = transparentItems.get(t.modelId)
      if (!set) {
        set = new Set()
        transparentItems.set(t.modelId, set)
      }
      set.add(t.localId)
    }
    useAppStore.getState().clearSelection()
    try {
      await highlighter.clear('select')
    } catch {
      /* ignore */
    }
    await this._reapplyFade()
  },
  async setItemsVisibility(
    modelId: string,
    ids: number[],
    visible: boolean,
  ): Promise<void> {
    if (ids.length === 0) return
    const model = models.get(modelId)
    if (!model) return
    try {
      await model.setVisible(ids, visible)
    } catch (err) {
      console.warn('[viewerApi] setItemsVisibility failed', err)
    }
    if (!visible) {
      useAppStore.getState().setVisibilityMode('hide')
    }
    await this._refreshVisibilityState()
  },
  // Snapshot of every hidden localId per loaded model. The spatial
  // tree calls this on hiddenCount changes to badge nodes that are
  // currently invisible.
  async getHiddenItemsMap(): Promise<Map<string, Set<number>>> {
    const out = new Map<string, Set<number>>()
    for (const [id, m] of models) {
      try {
        const hidden = await m.getItemsByVisibility(false)
        if (hidden.length > 0) out.set(id, new Set(hidden))
      } catch (err) {
        console.warn('[viewerApi] getHiddenItemsMap failed', err)
      }
    }
    return out
  },
  // Snapshot of every faded localId per model. Read-only mirror of
  // the internal transparentItems map (used by viewpoint capture).
  getTransparentItemsMap(): Map<string, Set<number>> {
    const out = new Map<string, Set<number>>()
    for (const [id, set] of transparentItems) {
      if (set.size > 0) out.set(id, new Set(set))
    }
    return out
  },
  // Replace the current set of faded items with the given map and
  // re-apply the 'fade' highlighter style. Used by viewpoint replay
  // to restore the captured transparency state.
  async setTransparentItems(
    map: Map<string, Set<number>>,
  ): Promise<void> {
    transparentItems.clear()
    for (const [id, set] of map) {
      if (set.size > 0) transparentItems.set(id, new Set(set))
    }
    if (highlighter) {
      try {
        await highlighter.clear('fade')
      } catch {
        /* ignore */
      }
    }
    if (transparentItems.size > 0) {
      await this._reapplyFade()
    } else {
      useAppStore.getState().setTransparentCount(0)
      this._invalidate()
    }
  },
  async clearTransparent(): Promise<void> {
    transparentItems.clear()
    await clearMaterialFadeStyles()
    if (highlighter) {
      try {
        await highlighter.clear('fade')
      } catch (err) {
        console.warn('[viewerApi] clear(fade) failed', err)
      }
    }
    useAppStore.getState().setTransparentCount(0)
    this._invalidate()
  },
  /** Pintar as IfcSpatialZone de um modelo como volumes translúcidos.
   *
   *  Uma zona (compartimento corta-fogo, zona térmica) atravessa salas
   *  e paredes; opaca, tapava tudo o que tem dentro. O importador só
   *  põe transparentes os IfcSpace. Um estilo do Highlighter, e não o
   *  setOpacity do modelo: este desaparecia na primeira vez que se
   *  seleccionava e desseleccionava qualquer coisa (medido). */
  async marcarZonas(modelId: string, ids: number[]): Promise<void> {
    if (ids.length === 0) return
    zonasPorModelo.set(modelId, new Set(ids))
    if (!highlighter) return
    try {
      await highlighter.highlightByID(ESTILO_DAS_ZONAS, { [modelId]: new Set(ids) }, false, false)
    } catch (err) {
      console.warn('[viewerApi] marcarZonas failed', err)
    }
    this._invalidate()
  },
  async _reapplyFade(): Promise<void> {
    if (!highlighter) return
    const map: Record<string, Set<number>> = {}
    let total = 0
    for (const [mid, set] of transparentItems) {
      if (set.size > 0) {
        map[mid] = new Set(set)
        total += set.size
      }
    }
    if (isFeatureEnabled('materialTransparency')) {
      await clearMaterialFadeStyles()
      try {
        await highlighter.clear('fade')
      } catch {
        // There may not be a base fade overlay yet.
      }
      let styleIndex = 0
      const fallback: Record<string, Set<number>> = {}
      for (const [modelId, ids] of transparentItems) {
        const model = models.get(modelId)
        if (!model || ids.size === 0) continue
        try {
          const groups = await model.getItemsMaterialDefinition([...ids])
          if (groups.length === 0) {
            fallback[modelId] = new Set(ids)
            continue
          }
          for (const group of groups) {
            if (group.localIds.length === 0) continue
            const style = `vn2r-fade-material-${styleIndex++}`
            const definition: MaterialDefinition = {
              ...group.definition,
              color: group.definition.color.clone(),
              opacity: useAppStore.getState().transparencyOpacity,
              transparent: true,
              preserveOriginalMaterial: true,
              customId: style,
            }
            highlighter.styles.set(style, definition)
            materialFadeStyles.add(style)
            await highlighter.highlightByID(
              style,
              { [modelId]: new Set(group.localIds) },
              true,
              false,
            )
          }
        } catch (err) {
          console.warn(
            '[viewerApi] material-aware transparency failed; using fallback',
            err,
          )
          fallback[modelId] = new Set(ids)
        }
      }
      if (Object.keys(fallback).length > 0) {
        await highlighter.highlightByID('fade', fallback, true, false)
      }
    } else {
      await clearMaterialFadeStyles()
      try {
        await highlighter.highlightByID('fade', map, true, false)
      } catch (err) {
        console.warn('[viewerApi] highlightByID(fade) failed', err)
      }
    }
    useAppStore.getState().setTransparentCount(total)
    this._invalidate()
  },
  async setTransparencyOpacity(value: number): Promise<void> {
    useAppStore.getState().setTransparencyOpacity(value)
    if (!highlighter) return
    const cur = highlighter.styles.get('fade')
    if (cur) {
      ;(cur as { opacity: number }).opacity =
        useAppStore.getState().transparencyOpacity
      highlighter.styles.set('fade', cur)
    }
    if (transparentItems.size > 0) {
      await this._reapplyFade()
    }
  },
  // Adjust the ghost overlay opacity live. Reads the current style
  // entry, mutates the opacity, and re-applies if ghost mode is active.
  // Persistence + clamping live in the slice; here we only push the
  // change into the highlighter material.
  async setGhostOpacity(value: number): Promise<void> {
    useAppStore.getState().setGhostOpacity(value)
    const hl = highlighter
    if (!hl) return
    const cur = hl.styles.get('ghost')
    if (cur) {
      // Mutate in place: same reference the highlighter caches.
      ;(cur as { opacity: number }).opacity = useAppStore.getState().ghostOpacity
      // Re-set on the DataMap to make sure any cached materials are
      // rebuilt. Some thatopen versions only honour value changes when
      // the entry is re-set.
      hl.styles.set('ghost', cur)
    }
    if (precisaDeFantasma()) {
      await this._applyGhost()
    }
  },
  // Called from the selection slice subscription so ghost mode tracks
  // selection changes (otherwise the previously-selected item stays
  // unghosted after a new pick).
  async refreshGhost(): Promise<void> {
    if (!precisaDeFantasma()) return
    await this._applyGhost()
  },
  // Full disposal: remove from scene, dispose Fragments, drop from
  // every cache (viewerApi, rawIfc, popups, store), and clear any
  // selection that pointed into the model.
  async disposeModel(
    modelId: string,
    opcoes?: { manterBytes?: string; manterNome?: string },
  ) {
    const model = models.get(modelId)
    // O registo na lista, lido ANTES de o tirar: o SHA e o nome dizem o
    // que sai da rede de recuperação (ver o fim desta função).
    const registo = useAppStore.getState().models.find((m) => m.id === modelId)
    if (model) {
      // As arestas que o useViewer pendurou nos tiles. O model.dispose()
      // do Fragments liberta a geometria e o material de cada tile, mas
      // não os filhos que lhes pendurámos: sem isto as EdgesGeometry
      // ficavam na placa gráfica a cada Remover, Substituir e Fechar
      // sessão. Tem de ser antes do dispose, que tira os tiles do object.
      model.object.traverse((o) => {
        if ((o as THREE.LineSegments).isLineSegments) {
          ;(o as THREE.LineSegments).geometry.dispose()
        }
      })
      ;(model as unknown as { __edgeMaterial?: THREE.Material }).__edgeMaterial?.dispose()
    }
    if (model && world) {
      world.scene.three.remove(model.object)
      try {
        await model.dispose()
      } catch (err) {
        console.warn('[viewerApi] model.dispose failed', err)
      }
    }
    esquecerModelo(modelId)
    models.delete(modelId)
    ancestorPaths.delete(modelId)
    descendantsMap.delete(modelId)
    rawIfc.unloadModel(modelId)
    void import('@viewer/lib/ifcDocuments').then(({ esquecerDocumentos }) =>
      esquecerDocumentos(modelId),
    )
    void import('@viewer/lib/classColors').then(({ forgetModel }) =>
      forgetModel(modelId),
    )
    void import('@viewer/lib/paintEngine').then(
      ({ detachPaintFromModel }) => detachPaintFromModel(modelId),
    )
    void import('@viewer/lib/edgeVisibility').then(
      ({ disposeEdgeVisibility }) => disposeEdgeVisibility(modelId),
    )
    // Caches das Vistas 2D: sem isto seguravam a instância descartada.
    void import('@viewer/views2d/generateForViewer').then(
      ({ clearCategoryCache }) => clearCategoryCache(modelId),
    )
    void import('@viewer/views2d/modelBox').then(({ clearWorldBoxCache }) =>
      clearWorldBoxCache(modelId),
    )
    const store = useAppStore.getState()
    store.closePopupsForModel(modelId)
    store.removeModel(modelId)
    if (store.selection?.modelId === modelId) store.clearSelection()
    // Rede de recuperação: um modelo que se tirou (Remover, Substituir)
    // deixa de ser trabalho da sessão. Os bytes saem da cache, a não ser
    // que outro modelo aberto venha do mesmo ficheiro, e o nome sai da
    // lista do manifesto. Sem isto o "Recuperar" reabria tudo o que
    // alguma vez se abriu, incluindo a versão substituída.
    if (registo) {
      const restantes = useAppStore.getState().models
      const sha = registo.sha256.toLowerCase()
      const shaEmUso =
        sha === opcoes?.manterBytes?.toLowerCase() ||
        restantes.some((m) => m.sha256.toLowerCase() === sha)
      const nomeEmUso =
        registo.fileName === opcoes?.manterNome ||
        restantes.some((m) => m.fileName === registo.fileName)
      try {
        const { esquecerIfc } = await import('@core/session/recovery')
        await esquecerIfc({
          sha256: shaEmUso ? null : sha,
          nome: nomeEmUso ? null : registo.fileName,
        })
      } catch (err) {
        console.warn('[viewerApi] recovery forget failed', err)
      }
    }
    notify()
  },
  // Set the orbit pivot to the centre of an element's bbox. Camera
  // position is unchanged (camera-controls' setOrbitPoint), so the
  // user's vantage stays put; only future orbits rotate around the
  // new point. Best for a quick "set centre, keep my view".
  async setOrbitToElement(modelId: string, localId: number) {
    if (!world) return
    const model = models.get(modelId)
    if (!model) return
    const boxes = await worldBoxesFor(model, [localId])
    const box = boxes[0]
    if (!box || box.isEmpty()) return
    const center = new THREE.Vector3()
    box.getCenter(center)
    world.camera.controls.setOrbitPoint(center.x, center.y, center.z)
  },
  // Same as setOrbitToElement, but ALSO reorients the camera to look
  // straight at the new pivot via setTarget. The camera position is
  // unchanged; only the look-direction rotates. This makes the new
  // pivot immediately "feel right" without having to zoom in. Use
  // this when the user explicitly asks to redefine the rotation axis.
  async setOrbitAndLookAtElement(modelId: string, localId: number) {
    if (!world) return
    const model = models.get(modelId)
    if (!model) return
    const boxes = await worldBoxesFor(model, [localId])
    const box = boxes[0]
    if (!box || box.isEmpty()) return
    const center = new THREE.Vector3()
    box.getCenter(center)
    // setTarget moves the orbit target AND makes the camera rotate to
    // keep looking at it. With enableTransition=true, camera-controls
    // smooth-interpolates over a few frames so the rotation isn't
    // jarring. Subsequent orbits use this point as the centre.
    await world.camera.controls.setTarget(
      center.x,
      center.y,
      center.z,
      true,
    )
  },
  // Convenience wrapper: pick the first selected element (or the
  // centroid of all selected) and set the pivot there with reorient.
  async setOrbitAndLookAtSelection() {
    if (!world) return
    const targets = useAppStore.getState().selectedTargets
    if (targets.length === 0) return
    const box = new THREE.Box3()
    let initialised = false
    const byModel = new Map<string, number[]>()
    for (const t of targets) {
      const list = byModel.get(t.modelId) ?? []
      list.push(t.localId)
      byModel.set(t.modelId, list)
    }
    for (const [modelId, ids] of byModel) {
      const model = models.get(modelId)
      if (!model) continue
      const boxes = await worldBoxesFor(model, ids)
      for (const b of boxes) {
        if (!b || b.isEmpty()) continue
        if (!initialised) {
          box.copy(b)
          initialised = true
        } else box.union(b)
      }
    }
    if (!initialised) return
    const center = new THREE.Vector3()
    box.getCenter(center)
    await world.camera.controls.setTarget(
      center.x,
      center.y,
      center.z,
      true,
    )
  },
  // Frame and pivot on the union of currently selected elements.
  // Uses camera-controls.fitToBox which both moves the camera to a
  // fitting distance AND places the target at the box centre, so
  // subsequent orbiting rotates around the selection.
  async focusOnSelection() {
    if (!world) return
    const targets = useAppStore.getState().selectedTargets
    if (targets.length === 0) {
      // No selection: fall back to fitting all models.
      void this.fitToAllModels()
      return
    }
    const box = new THREE.Box3()
    let initialised = false
    // Group localIds per model to make a single getBoxes call.
    const byModel = new Map<string, number[]>()
    for (const t of targets) {
      const list = byModel.get(t.modelId) ?? []
      list.push(t.localId)
      byModel.set(t.modelId, list)
    }
    for (const [modelId, ids] of byModel) {
      const model = models.get(modelId)
      if (!model) continue
      const boxes = await worldBoxesFor(model, ids)
      for (const b of boxes) {
        if (!b || b.isEmpty()) continue
        if (!initialised) {
          box.copy(b)
          initialised = true
        } else box.union(b)
      }
    }
    if (!initialised) return
    void world.camera.controls
      .fitToBox(box, true, {
        paddingTop: 0.2,
        paddingBottom: 0.2,
        paddingLeft: 0.2,
        paddingRight: 0.2,
      })
      .catch((err: unknown) => {
        console.warn('[viewerApi] fitToBox(selection) failed', err)
      })
  },
  // Frame the union of an arbitrary set of items WITHOUT touching the
  // selection. Used by the diff panel's "Focar nas diferenças" so the
  // camera encloses every changed element without selecting them all
  // (selecting thousands of items would hammer the highlighter).
  async fitToItems(
    targets: Array<{ modelId: string; localId: number }>,
  ): Promise<void> {
    if (!world || targets.length === 0) return
    const byModel = new Map<string, number[]>()
    for (const t of targets) {
      const list = byModel.get(t.modelId) ?? []
      list.push(t.localId)
      byModel.set(t.modelId, list)
    }
    const box = new THREE.Box3()
    let initialised = false
    for (const [modelId, ids] of byModel) {
      const model = models.get(modelId)
      if (!model) continue
      const boxes = await worldBoxesFor(model, ids)
      for (const b of boxes) {
        if (!b || b.isEmpty()) continue
        if (!initialised) {
          box.copy(b)
          initialised = true
        } else box.union(b)
      }
    }
    if (!initialised) return
    void world.camera.controls
      .fitToBox(box, true, {
        paddingTop: 0.2,
        paddingBottom: 0.2,
        paddingLeft: 0.2,
        paddingRight: 0.2,
      })
      .catch((err: unknown) => {
        console.warn('[viewerApi] fitToItems failed', err)
      })
  },
  // A caixa envolvente, no mundo, de um conjunto de itens, sem mexer na
  // selecção nem na câmara. Null quando nenhum tem geometria. Usada pela
  // caixa de corte à volta dos elementos de um tópico BCF.
  async getItemsWorldBox(
    targets: Array<{ modelId: string; localId: number }>,
  ): Promise<THREE.Box3 | null> {
    const byModel = new Map<string, number[]>()
    for (const t of targets) {
      const list = byModel.get(t.modelId) ?? []
      list.push(t.localId)
      byModel.set(t.modelId, list)
    }
    const box = new THREE.Box3()
    let initialised = false
    for (const [modelId, ids] of byModel) {
      const model = models.get(modelId)
      if (!model) continue
      const boxes = await worldBoxesFor(model, ids)
      for (const b of boxes) {
        if (!b || b.isEmpty()) continue
        if (!initialised) {
          box.copy(b)
          initialised = true
        } else box.union(b)
      }
    }
    return initialised ? box : null
  },
  // Camera fit over the union of all loaded *visible* model boxes.
  // We use Fragments-provided `model.box` (it knows about its tiled
  // geometry which Box3.setFromObject misses) and delegate the
  // distance computation to camera-controls.fitToBox which accounts
  // for camera FOV. `model.box` lives in each model's LOCAL frame;
  // when the loader bakes an IfcMapConversion onto model.object
  // (georreferenciação de federação) the local boxes need to be
  // transformed into world coords before unioning, otherwise two
  // models with different map origins would all fit around the same
  // local-frame box.
  async fitToAllModels() {
    if (!world) return
    const box = new THREE.Box3()
    let initialised = false
    for (const m of models.values()) {
      if (!m.object.visible) continue
      // resolveRobustFitBox trata da ambiguidade do model.box (por vezes
      // já vem em coords de MUNDO; aplicar matrixWorld às cegas mandava a
      // câmara para 2x a posição em modelos georreferenciados) E ignora
      // elementos-outlier (marco de federação à cota 0, textos 3D) que
      // esticavam a bbox e faziam a câmara apontar para espaço vazio.
      const mb = await resolveRobustFitBox(m)
      if (!mb) continue
      if (!initialised) {
        box.copy(mb)
        initialised = true
      } else box.union(mb)
    }
    if (!initialised) return
    void world.camera.controls
      .fitToBox(box, true, {
        paddingTop: 0.1,
        paddingBottom: 0.1,
        paddingLeft: 0.1,
        paddingRight: 0.1,
      })
      .catch((err: unknown) => {
        console.warn('[viewerApi] fitToBox failed', err)
      })
  },
  // Imperatively select an item from outside the canvas (spatial tree
  // click, search, inverse relations, etc). Always sets the global
  // selection in zustand directly because the Highlighter only fires
  // onHighlight for entities with geometry (storeys, sites, types
  // etc. would otherwise leave the panel out of sync).
  async selectByLocalId(modelId: string, localId: number, zoom = false) {
    useAppStore.getState().setSelection({ modelId, localId })
    const h = highlighter
    if (!h) return
    try {
      await h.highlightByID(
        'select',
        { [modelId]: new Set([localId]) },
        true,
        zoom,
      )
    } catch (err) {
      // Non-geometric entities throw: that's fine, the store
      // selection is already updated.
      console.debug('[viewerApi] highlightByID skipped', err)
    }
  },
  subscribe(fn: () => void): () => void {
    listeners.add(fn)
    return () => {
      listeners.delete(fn)
    }
  },
}
